import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CheckCircle, XCircle, ClipboardText, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { EmployeeName } from '@/components/EmployeeName';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const REASON_LABELS_AR = {
  forgot: 'نسيان البصمة', technical_issue: 'عطل تقني',
  work_errand: 'خروج لمهمة عمل', emergency: 'ظرف طارئ', other: 'أخرى',
};
const REASON_LABELS_EN = {
  forgot: 'Forgot', technical_issue: 'Technical issue',
  work_errand: 'Work errand', emergency: 'Emergency', other: 'Other',
};
const TYPE_LABEL = {
  clock_in:  { ar: 'حضور',  en: 'Clock-in' },
  clock_out: { ar: 'انصراف', en: 'Clock-out' },
  full_day:  { ar: 'يوم كامل', en: 'Full Day' },
};
const STATUS_BADGE = {
  pending:   { ar: 'قيد المراجعة', en: 'Pending',   cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved:  { ar: 'مقبول',        en: 'Approved',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected:  { ar: 'مرفوض',        en: 'Rejected',  cls: 'bg-red-100 text-red-800 border-red-200' },
  cancelled: { ar: 'ملغي',         en: 'Cancelled', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

export default function AdminAttendanceCorrections({ embed = false }) {
  const navigate = useNavigate();
  const { language, direction } = useLanguage();
  const { empNo } = useEmployeesMap();
  const { getBadge } = useEmployeeBadges();
  const { isAdmin } = useAuth();
  const isAr = language === 'ar';

  const [pending, setPending] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API}/attendance-corrections/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
        isAdmin
          ? axios.get(`${API}/attendance-corrections/all`, { withCredentials: true }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setPending(p.data || []);
      setAllRows(a.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [isAdmin]);

  const handleApprove = async (id) => {
    if (!window.confirm(isAr
      ? 'سيتم تحديث سجل الحضور واحتساب الإضافي/الجزاء بناءً على الأوقات المصحّحة. هل تريد المتابعة؟'
      : 'The attendance record will be updated and overtime/penalty will be recomputed from the corrected times. Continue?')) return;
    try {
      const { data } = await axios.put(
        `${API}/attendance-corrections/${id}/approve`, {}, { withCredentials: true }
      );
      const applied = data.applied_penalty || 0;
      toast.success(
        applied > 0
          ? (isAr ? `تم الاعتماد. الجزاء المُحتسب: ${applied} ريال` : `Approved. Applied penalty: ${applied} SAR`)
          : (isAr ? 'تم الاعتماد. لا يوجد جزاء على الأوقات المصحّحة.' : 'Approved. No penalty resulted from the corrected times.')
      );
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر الاعتماد' : 'Approval failed'));
    }
  };

  const openReject = (row) => {
    setRejectTarget(row);
    setRejectReason('');
    setRejectOpen(true);
  };
  const submitReject = async () => {
    if (!rejectReason || rejectReason.trim().length < 3) {
      toast.error(isAr ? 'يرجى كتابة سبب الرفض' : 'Rejection reason required');
      return;
    }
    try {
      await axios.put(
        `${API}/attendance-corrections/${rejectTarget.id}/reject`,
        { reason: rejectReason.trim() },
        { withCredentials: true }
      );
      toast.success(isAr ? 'تم الرفض' : 'Rejected');
      setRejectOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'تعذّر الرفض' : 'Rejection failed'));
    }
  };

  const renderRows = (rows, withActions) => {
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={withActions ? 8 : 7} className="text-center py-8 text-[#655dc6]/70">
            {isAr ? 'لا توجد طلبات' : 'No requests'}
          </TableCell>
        </TableRow>
      );
    }
    return rows.map((r) => {
      const tl = TYPE_LABEL[r.correction_type] || { ar: r.correction_type, en: r.correction_type };
      const s = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
      return (
        <TableRow key={r.id} className="border-[#C4BFF0]">
          <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(r.user_id)}</TableCell>
          <TableCell><EmployeeName name={r.user_name} nameEn={r.user_name_en} badgeData={getBadge(r.user_id)} badgeLanguage={language} /></TableCell>
          <TableCell className="text-[#655dc6]/80">{r.target_date}</TableCell>
          <TableCell className="text-[#655dc6]/80">{isAr ? tl.ar : tl.en}</TableCell>
          <TableCell className="text-[#655dc6]/70 text-xs">
            {r.requested_clock_in && <div>{isAr ? 'حضور' : 'In'}: {r.requested_clock_in}</div>}
            {r.requested_clock_out && <div>{isAr ? 'انصراف' : 'Out'}: {r.requested_clock_out}</div>}
          </TableCell>
          <TableCell className="text-[#655dc6]/70 text-xs max-w-xs">
            <div className="font-semibold">
              {isAr ? REASON_LABELS_AR[r.reason_code] : REASON_LABELS_EN[r.reason_code]}
            </div>
            <div className="text-[10px] opacity-80">{r.reason_text}</div>
            {r.status === 'rejected' && r.rejection_reason && (
              <div className="text-[10px] text-red-600 mt-1">
                {isAr ? 'سبب الرفض' : 'Rejection'}: {r.rejection_reason}
              </div>
            )}
            {r.status === 'approved' && r.applied_penalty_amount > 0 && (
              <div className="text-[10px] text-amber-700 mt-1">
                {isAr ? 'جزاء مُحتسب: ' : 'Applied penalty: '}{r.applied_penalty_amount} SAR
              </div>
            )}
            {r.status === 'approved' && (r.applied_penalty_amount === 0 || r.applied_penalty_amount == null) && r.waived_penalty_amount > 0 && (
              <div className="text-[10px] text-emerald-700 mt-1">
                {isAr ? 'الجزاء المُلغى: ' : 'Waived: '}{r.waived_penalty_amount} SAR
              </div>
            )}
          </TableCell>
          <TableCell><Badge className={s.cls}>{isAr ? s.ar : s.en}</Badge></TableCell>
          {withActions && (
            <TableCell>
              {r.status === 'pending' ? (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm" onClick={() => handleApprove(r.id)}
                    className="text-emerald-600 hover:bg-emerald-50 gap-1"
                    data-testid={`approve-${r.id}`}
                  >
                    <CheckCircle size={14} weight="fill" />
                    {isAr ? 'اعتماد' : 'Approve'}
                  </Button>
                  <Button
                    variant="ghost" size="sm" onClick={() => openReject(r)}
                    className="text-red-600 hover:bg-red-50 gap-1"
                    data-testid={`reject-${r.id}`}
                  >
                    <XCircle size={14} weight="fill" />
                    {isAr ? 'رفض' : 'Reject'}
                  </Button>
                </div>
              ) : (
                <span className="text-[10px] text-[#655dc6]/50">
                  {r.reviewed_by_name || '-'}
                </span>
              )}
            </TableCell>
          )}
        </TableRow>
      );
    });
  };

  // Reusable inner content (table + tabs + dialog) — used by both standalone
  // page mode AND the embedded mode inside the unified Attendance page.
  const inner = (
    <>
        <Card className="border border-[#C4BFF0]">
          <CardContent className="pt-6">
            <Tabs value={tab} onValueChange={setTab} dir={direction}>
              <TabsList>
                <TabsTrigger value="pending" data-testid="tab-pending">
                  {isAr ? `قيد المراجعة (${pending.length})` : `Pending (${pending.length})`}
                </TabsTrigger>
                {isAdmin && (
                  <TabsTrigger value="all" data-testid="tab-all">
                    {isAr ? `كل الطلبات (${allRows.length})` : `All (${allRows.length})`}
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="pending" className="mt-4">
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="pending-table">
                      <TableHeader>
                        <TableRow className="border-[#C4BFF0]">
                          <TableHead className="text-start">{isAr ? 'الرقم الوظيفي' : 'Emp #'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'التاريخ' : 'Date'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'النوع' : 'Type'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الأوقات' : 'Times'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الحالة' : 'Status'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'إجراء' : 'Action'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>{renderRows(pending, true)}</TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>

              {isAdmin && (
                <TabsContent value="all" className="mt-4">
                  <div className="overflow-x-auto">
                    <Table data-testid="all-table">
                      <TableHeader>
                        <TableRow className="border-[#C4BFF0]">
                          <TableHead className="text-start">{isAr ? 'الرقم الوظيفي' : 'Emp #'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'التاريخ' : 'Date'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'النوع' : 'Type'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الأوقات' : 'Times'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'الحالة' : 'Status'}</TableHead>
                          <TableHead className="text-start">{isAr ? 'إجراء' : 'Action'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>{renderRows(allRows, true)}</TableBody>
                    </Table>
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">{isAr ? 'رفض الطلب' : 'Reject Request'}</DialogTitle>
            <DialogDescription className="text-xs">
              {isAr ? 'سيُبلَّغ الموظف بسبب الرفض' : 'The employee will be notified of the reason.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={isAr ? 'اكتب سبب الرفض (3 أحرف على الأقل)' : 'Rejection reason (min 3 chars)'}
            rows={4}
            data-testid="reject-reason-input"
          />
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setRejectOpen(false)}>{isAr ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={submitReject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-btn">
              {isAr ? 'تأكيد الرفض' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // Embedded inside Attendance page — caller already provides header/wrapper.
  if (embed) return inner;

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="admin-corrections-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-2">
          <ClipboardText size={32} weight="duotone" />
          {isAr ? 'مراجعة طلبات تصحيح البصمة' : 'Attendance Correction Approvals'}
        </h1>
        {inner}
      </main>
    </div>
  );
}
